import { useState, useEffect } from "react";
import { X, ChevronRight, ChevronLeft, Building2, FileText, CalendarCheck } from "lucide-react";
import { Select } from "antd";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ONBOARDED_URL, CUSTOMER_TYPE_MASTER_URL } from "../../constants/api";
import { State, City } from "country-state-city";

const STEPS = [
  { id: 1, label: "Client Info",        icon: Building2     },
  { id: 2, label: "Financial Details",  icon: FileText      },
  { id: 3, label: "Onboarding Details", icon: CalendarCheck },
];

const INITIAL = {
  // Step 1
  org_name:        "",
  client_type:     "",
  state:           "",
  city:            "",
  address:         "",
  contact_name:    "",
  contact_number:  "",
  contact_email:   "",
  contact_desig:   "",

  // Step 2
  product_sold:       "",
  contract_value:     "",
  payment_terms:      "",

  // Step 3
  onboarded_date:       "",
  go_live_date:         "",
  onboarding_status:    "In Progress",
  implementation_owner_id: "",
  assigned_to_id:       "",
  remarks:              "",

  // AMC
  amc_start_date:        "",
  amc_end_date:          "",
  amc_value:             "",
  amc_percentage:        "",
  amc_payment_terms:     "Annual",
  amc_payment_status:    "Pending",
  renewal_reminder_days: "30",
};

const LabelInput = ({ label, required, children }) => (
  <div className="space-y-1">
    <label className="text-xs font-medium text-foreground">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

export default function AddOnboardedModal({ onClose, onSuccess, users = [], initialData = {}, customerId = null }) {
  const { create, fetch } = useApiMethods();
  const [step,        setStep]       = useState(1);
  const [form,        setForm]       = useState({ ...INITIAL, ...initialData });
  const [loading,     setLoading]    = useState(false);
  const [errors,      setErrors]     = useState({});
  const [clientTypes, setClientTypes] = useState([]);

  useEffect(() => {
    fetch(CUSTOMER_TYPE_MASTER_URL).then(res => {
      if (res?.status === "success") {
        setClientTypes((res.data || []).filter(t => !t.is_channel_partner && t.is_active !== false));
      }
    });
  }, []);

  const states = State.getStatesOfCountry("IN");
  const cities = form.state ? City.getCitiesOfState("IN", form.state) : [];

  const set = (key, val) => {
    setForm(prev => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: "" }));
  };

  const validateStep = () => {
    const errs = {};
    if (step === 1) {
      if (!(form.org_name || "").trim()) errs.org_name = "Organization name is required";

      // Primary contact is mandatory. 84 of the first 137 onboarded clients were
      // saved with no contact at all, which left the client list report unable to
      // answer "who do we call". Format is checked too — a required field that
      // accepts "x" collects nothing useful.
      if (!(form.contact_name || "").trim()) {
        errs.contact_name = "Contact name is required";
      }
      if (!(form.contact_desig || "").trim()) {
        errs.contact_desig = "Designation is required";
      }

      const phone = (form.contact_number || "").replace(/[\s-]/g, "");
      if (!phone) {
        errs.contact_number = "Phone number is required";
      } else if (!/^(\+?91)?[6-9]\d{9}$/.test(phone)) {
        errs.contact_number = "Enter a 10-digit mobile number";
      }

      const email = (form.contact_email || "").trim();
      if (!email) {
        errs.contact_email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        errs.contact_email = "Enter a valid email address";
      }
    }
    if (step === 3) {
      if (!form.onboarded_date) errs.onboarded_date = "Onboarded date is required";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = () => { if (validateStep()) setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    try {
      const contactName = (form.contact_name || "").trim();
      const payload = {
        org_name:             (form.org_name || "").trim(),
        client_type:          form.client_type,
        state:                form.state,
        city:                 form.city,
        address:              form.address,
        product_sold:         form.product_sold,
        contract_value:       form.contract_value || 0,
        payment_terms:        form.payment_terms,
        onboarded_date:       form.onboarded_date || null,
        go_live_date:         form.go_live_date || null,
        onboarding_status:    form.onboarding_status,
        implementation_owner_id: form.implementation_owner_id || null,
        assigned_to_id:       form.assigned_to_id || null,
        remarks:              form.remarks,
        ...(customerId ? { customer_id: customerId } : {}),
        ...(form.quotation_plan_id ? { quotation_plan_id: form.quotation_plan_id } : {}),
      };

      // Contact — validateStep() guarantees these are present and well-formed
      // before submit, so this is no longer conditional on a name being typed.
      if (contactName) {
        payload.contacts = [{
          name:           contactName,
          designation:    (form.contact_desig || "").trim(),
          contact_number: (form.contact_number || "").replace(/[\s-]/g, ""),
          email:          (form.contact_email || "").trim(),
          is_primary:     true,
        }];
      }

      // AMC
      if (form.amc_start_date && form.amc_end_date) {
        payload.amc = {
          amc_start_date:        form.amc_start_date,
          amc_end_date:          form.amc_end_date,
          amc_value:             form.amc_value || 0,
          amc_percentage:        form.amc_percentage || null,
          payment_terms:         form.amc_payment_terms,
          payment_status:        form.amc_payment_status,
          renewal_reminder_days: parseInt(form.renewal_reminder_days) || 30,
        };
      }

      const res = await create(ONBOARDED_URL, payload);
      if (res?.status === "success") {
        onSuccess(res.id);
      } else if (res?.onboarded_id) {
        // Customer is already onboarded — route to existing record instead of erroring.
        ToastMsg("This lead already has an onboarded record — opening it.", "info");
        onSuccess(res.onboarded_id);
      } else {
        ToastMsg(res?.message || "Failed to save", "error");
      }
    } catch (e) {
      console.error("AddOnboardedModal submit error:", e);
      ToastMsg("Something went wrong. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const userOptions = users.map(u => ({
    value: String(u.id),
    label: `${u.login_fname || ""} ${u.login_lname || ""}`.trim(),
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div>
            <h2 className="text-base font-semibold text-foreground">Add Onboarded Client</h2>
            <p className="text-xs text-muted-foreground">Step {step} of {STEPS.length}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-0 px-6 py-3 border-b border-border bg-muted/20 shrink-0">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center flex-1">
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                step === s.id   ? "bg-primary text-primary-foreground" :
                step > s.id    ? "text-green-600 dark:text-green-400"  :
                "text-muted-foreground"
              }`}>
                <s.icon className="h-3.5 w-3.5" />
                <span>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground mx-1 shrink-0" />
              )}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5">

          {/* ── Step 1: Client Info ──────────────────────────────────── */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <LabelInput label="Organization / Client Name" required>
                    <Input
                      placeholder="e.g. Apollo Hospitals Ltd"
                      value={form.org_name}
                      onChange={e => set("org_name", e.target.value)}
                      className={errors.org_name ? "border-red-500" : ""}
                    />
                    {errors.org_name && <p className="text-xs text-red-500 mt-0.5">{errors.org_name}</p>}
                  </LabelInput>
                </div>
                <LabelInput label="Client Type">
                  <Select
                    placeholder="Select type"
                    className="w-full"
                    value={form.client_type || undefined}
                    onChange={v => set("client_type", v || "")}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={clientTypes.map(t => ({ value: t.name, label: t.name }))}
                  />
                </LabelInput>
                <LabelInput label="State">
                  <Select
                    showSearch
                    placeholder="Select state"
                    className="w-full"
                    value={form.state || undefined}
                    onChange={v => { set("state", v || ""); set("city", ""); }}
                    allowClear
                    optionFilterProp="label"
                    options={states.map(s => ({ value: s.isoCode, label: s.name }))}
                  />
                </LabelInput>
                <LabelInput label="City">
                  <Select
                    showSearch
                    placeholder="Select city"
                    className="w-full"
                    value={form.city || undefined}
                    onChange={v => set("city", v || "")}
                    allowClear
                    optionFilterProp="label"
                    options={cities.map(c => ({ value: c.name, label: c.name }))}
                  />
                </LabelInput>
                <LabelInput label="Address">
                  <Input placeholder="Full address" value={form.address} onChange={e => set("address", e.target.value)} />
                </LabelInput>
              </div>

              <div className="border-t border-border pt-4">
                <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wide">Primary Contact</p>
                <div className="grid grid-cols-2 gap-4">
                  <LabelInput label="Contact Name" required>
                    <Input placeholder="Name" value={form.contact_name}
                      onChange={e => set("contact_name", e.target.value)}
                      className={errors.contact_name ? "border-red-500" : ""} />
                    {errors.contact_name && <p className="text-xs text-red-500 mt-0.5">{errors.contact_name}</p>}
                  </LabelInput>
                  <LabelInput label="Designation" required>
                    <Input placeholder="e.g. IT Manager" value={form.contact_desig}
                      onChange={e => set("contact_desig", e.target.value)}
                      className={errors.contact_desig ? "border-red-500" : ""} />
                    {errors.contact_desig && <p className="text-xs text-red-500 mt-0.5">{errors.contact_desig}</p>}
                  </LabelInput>
                  <LabelInput label="Phone" required>
                    <Input placeholder="10-digit mobile number" value={form.contact_number}
                      onChange={e => set("contact_number", e.target.value)}
                      className={errors.contact_number ? "border-red-500" : ""} />
                    {errors.contact_number && <p className="text-xs text-red-500 mt-0.5">{errors.contact_number}</p>}
                  </LabelInput>
                  <LabelInput label="Email" required>
                    <Input type="email" placeholder="Email address" value={form.contact_email}
                      onChange={e => set("contact_email", e.target.value)}
                      className={errors.contact_email ? "border-red-500" : ""} />
                    {errors.contact_email && <p className="text-xs text-red-500 mt-0.5">{errors.contact_email}</p>}
                  </LabelInput>
                </div>
              </div>
            </div>
          )}

          {/* ── Step 2: Financial Details ─────────────────────────────── */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <LabelInput label="Products / Services Sold">
                    <Input placeholder="e.g. CRM, ERP, Consulting, Software License" value={form.product_sold} onChange={e => set("product_sold", e.target.value)} />
                  </LabelInput>
                </div>
                <LabelInput label="Contract Value (₹)">
                  <Input type="number" placeholder="e.g. 500000" value={form.contract_value} onChange={e => set("contract_value", e.target.value)} />
                </LabelInput>
                <LabelInput label="Payment Terms">
                  <Select
                    placeholder="Select terms"
                    className="w-full"
                    value={form.payment_terms || undefined}
                    onChange={v => set("payment_terms", v || "")}
                    allowClear
                    options={[
                      { value: "Annual",      label: "Annual"      },
                      { value: "Half-yearly", label: "Half-yearly" },
                      { value: "Quarterly",   label: "Quarterly"   },
                      { value: "Monthly",     label: "Monthly"     },
                      { value: "One-time",    label: "One-time"    },
                    ]}
                  />
                </LabelInput>
              </div>

              {/* AMC Section */}
              <div className="border-t border-border pt-4">
                <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                  AMC Details <span className="normal-case font-normal">(optional — can be added later)</span>
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <LabelInput label="AMC Start Date">
                    <Input type="date" value={form.amc_start_date} onChange={e => set("amc_start_date", e.target.value)} />
                  </LabelInput>
                  <LabelInput label="AMC End Date">
                    <Input type="date" value={form.amc_end_date} onChange={e => set("amc_end_date", e.target.value)} />
                  </LabelInput>
                  <LabelInput label="AMC % of Product Cost">
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="e.g. 15"
                      value={form.amc_percentage}
                      onChange={e => {
                        const v = e.target.value;
                        const cv = parseFloat(form.contract_value || 0);
                        const pct = parseFloat(v);
                        const calc = (!isNaN(pct) && cv) ? (cv * pct / 100).toFixed(2) : "";
                        setForm(p => ({ ...p, amc_percentage: v, amc_value: calc || p.amc_value }));
                      }}
                    />
                  </LabelInput>
                  <LabelInput label="AMC Value (₹)">
                    <Input
                      type="number"
                      placeholder="e.g. 50000"
                      value={form.amc_value}
                      onChange={e => setForm(p => ({ ...p, amc_value: e.target.value, amc_percentage: "" }))}
                    />
                  </LabelInput>
                  <LabelInput label="AMC Payment Terms">
                    <Select
                      className="w-full"
                      value={form.amc_payment_terms}
                      onChange={v => set("amc_payment_terms", v)}
                      options={[
                        { value: "Annual",      label: "Annual"      },
                        { value: "Half-yearly", label: "Half-yearly" },
                        { value: "Quarterly",   label: "Quarterly"   },
                        { value: "Monthly",     label: "Monthly"     },
                      ]}
                    />
                  </LabelInput>
                  <LabelInput label="AMC Payment Status">
                    <Select
                      className="w-full"
                      value={form.amc_payment_status}
                      onChange={v => set("amc_payment_status", v)}
                      options={[
                        { value: "Paid",    label: "Paid"    },
                        { value: "Pending", label: "Pending" },
                        { value: "Overdue", label: "Overdue" },
                        { value: "Waived",  label: "Waived"  },
                      ]}
                    />
                  </LabelInput>
                  <LabelInput label="Renewal Reminder (days before expiry)">
                    <Input type="number" value={form.renewal_reminder_days} onChange={e => set("renewal_reminder_days", e.target.value)} />
                  </LabelInput>
                </div>
              </div>
            </div>
          )}

          {/* ── Step 3: Onboarding Details ────────────────────────────── */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <LabelInput label="Onboarded Date" required>
                  <Input
                    type="date"
                    value={form.onboarded_date}
                    onChange={e => set("onboarded_date", e.target.value)}
                    className={errors.onboarded_date ? "border-red-500" : ""}
                  />
                  {errors.onboarded_date && <p className="text-xs text-red-500 mt-0.5">{errors.onboarded_date}</p>}
                </LabelInput>
                <LabelInput label="Go Live Date">
                  <Input type="date" value={form.go_live_date} onChange={e => set("go_live_date", e.target.value)} />
                </LabelInput>
                <LabelInput label="Onboarding Status">
                  <Select
                    className="w-full"
                    value={form.onboarding_status}
                    onChange={v => set("onboarding_status", v)}
                    options={[
                      { value: "In Progress", label: "In Progress" },
                      { value: "Go Live",     label: "Go Live"     },
                      { value: "Completed",   label: "Completed"   },
                      { value: "Suspended",   label: "Suspended"   },
                    ]}
                  />
                </LabelInput>
                <LabelInput label="Implementation Owner">
                  <Select
                    showSearch
                    placeholder="Select owner"
                    className="w-full"
                    value={form.implementation_owner_id || undefined}
                    onChange={v => set("implementation_owner_id", v || "")}
                    allowClear
                    optionFilterProp="label"
                    options={userOptions}
                  />
                </LabelInput>
                <LabelInput label="Assigned To (Account Manager)">
                  <Select
                    showSearch
                    placeholder="Select user"
                    className="w-full"
                    value={form.assigned_to_id || undefined}
                    onChange={v => set("assigned_to_id", v || "")}
                    allowClear
                    optionFilterProp="label"
                    options={userOptions}
                  />
                </LabelInput>
                <div className="col-span-2">
                  <LabelInput label="Remarks">
                    <Textarea placeholder="Any additional notes..." value={form.remarks} onChange={e => set("remarks", e.target.value)} rows={2} />
                  </LabelInput>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border shrink-0">
          <Button variant="outline" onClick={step === 1 ? onClose : back} disabled={loading}>
            {step === 1 ? "Cancel" : (
              <><ChevronLeft className="h-4 w-4 mr-1" /> Back</>
            )}
          </Button>
          <div className="flex items-center gap-1">
            {STEPS.map(s => (
              <div key={s.id} className={`h-1.5 rounded-full transition-all ${
                step === s.id ? "w-6 bg-primary" : step > s.id ? "w-4 bg-primary/40" : "w-4 bg-muted"
              }`} />
            ))}
          </div>
          {step < 3 ? (
            <Button onClick={next}>
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? "Saving..." : "Save Client"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
